import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRequest } from '~/lib/useRequest';
import { config } from '~/config';
import { hasKey } from '~/utils';
import { ConversionTableResource } from '@ab/ams-school-api/types/private/v1';
import { ConversionTableRule } from '@ab/ams-school-api/src/services/content/v1/ConversionTables/types';

type ConversionTableAttributesPatch = {
  name?: string;
  sourceInstitutionName?: string | null;
  sourceCountryCode?: string | null;
  sourceEducationLevel?: string | null;
};

interface UpdateConversionTableRulesParams {
  id: string;
  rules: ConversionTableRule[];
  attributes?: ConversionTableAttributesPatch;
}

type UpdateConversionTableRulesResponse = {
  data: ConversionTableResource;
};

export function useUpdateConversionTableRules() {
  const request = useRequest();
  const queryClient = useQueryClient();

  const { mutateAsync, isLoading, error } = useMutation(
    ['updateConversionTableRules'],
    async ({ id, rules, attributes }: UpdateConversionTableRulesParams) => {
      const response = await request<UpdateConversionTableRulesResponse>(
        `${config.apiHost}/private/v1/conversion_tables/${id}`,
        {
          method: 'PATCH',
          body: {
            data: {
              type: 'conversion_table',
              id,
              attributes: {
                ...attributes,
                rules,
              },
            },
          },
          isExpectedResponse,
        }
      );

      queryClient.invalidateQueries({ queryKey: ['conversionTable', id] });
      return response.data;
    }
  );

  return {
    updateConversionTableRules: mutateAsync,
    isUpdating: isLoading,
    error,
  };
}

function isExpectedResponse(res: unknown): res is UpdateConversionTableRulesResponse {
  return Boolean(res && typeof res === 'object' && hasKey('data', res));
}
